/**
 * Mock projects data layer.
 *
 * The mock store below is shaped like the **API DTOs** (snake-free enums, ids,
 * timestamps, voting period, hero media). The exported functions map those DTOs
 * to the CMS view model, so swapping the mock for the real fetch keeps the
 * mapper and leaves the UI untouched.
 */

import { sdk } from '@needful-things-mvp/sdk';

import { type VotingPeriodDto, formatVotingPeriod, hoursSince, relativeTime } from './api-format';
import { USE_MOCK } from './data-mode';
import { unwrap } from './http';
import type { MediaRef } from './media';
import { type Paginated, delay } from './mock';
import { pushActivity } from './notifications';

export type ProjectStatus = 'Coming Soon' | 'Voting' | 'Winner Selected' | 'Production' | 'Archive';
export type ProjectApiStatus =
  | 'coming_soon'
  | 'voting'
  | 'winner_selected'
  | 'production'
  | 'archive';

export type OrderKey = 'newest' | 'name' | 'votes-desc' | 'votes-asc';

export const PROJECT_STATUS_LABEL: Record<ProjectApiStatus, ProjectStatus> = {
  coming_soon: 'Coming Soon',
  voting: 'Voting',
  winner_selected: 'Winner Selected',
  production: 'Production',
  archive: 'Archive',
};

export const PROJECT_STATUS_API: Record<ProjectStatus, ProjectApiStatus> = {
  'Coming Soon': 'coming_soon',
  Voting: 'voting',
  'Winner Selected': 'winner_selected',
  Production: 'production',
  Archive: 'archive',
};

/** API shape returned by `GET /admin/projects`. */
export interface ProjectDto {
  id: string;
  createdAt: string;
  updatedAt: string;
  slug: string;
  name: string;
  description: string | null;
  status: ProjectApiStatus;
  coverUrl: string | null;
  artistCount: number;
  votesCount: number;
  votingPeriod: VotingPeriodDto | null;
  heroMedia: MediaRef | null;
  /** Only present on the detail response (`media: { hero, gallery }`). */
  media?: { hero: MediaRef | null; gallery: MediaRef[] };
}

export interface Project {
  id: string;
  slug: string;
  name: string;
  description: string;
  status: ProjectStatus;
  artists: number;
  votes: number;
  period: string;
  /** Cover image (media library `Hero` usage). */
  heroMedia?: MediaRef | null;
  /** Ordered gallery (media library `Gallery` usages). */
  gallery?: MediaRef[];
  updated: string;
  updatedHours: number;
}

export interface ProjectInput {
  name: string;
  status: string;
  description: string;
  votingStarts: string;
  votingEnds: string;
  heroMedia?: MediaRef | null;
  gallery?: MediaRef[];
}

export interface ListProjectsParams {
  status?: ProjectStatus | 'All';
  order?: OrderKey;
  page?: number;
  pageSize?: number;
}

export interface ProjectQuery extends ListProjectsParams {
  search?: string;
}

export interface PaginatedProjects {
  items: Project[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export const PROJECT_PAGE_SIZE = 5;
export const PROJECT_PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

function isoHoursAgo(hours: number): string {
  return new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
}

function period(opensAt: string, closesAt: string | null): VotingPeriodDto {
  return { id: `period-${opensAt}`, projectId: '', mode: 'scheduled', opensAt, closesAt };
}

// ---- Mock "API" store (DTO-shaped) ----

let db: ProjectDto[] = [
  {
    id: 'bloom',
    createdAt: isoHoursAgo(24 * 60),
    updatedAt: isoHoursAgo(2),
    slug: 'bloom',
    name: 'Bloom',
    description: 'A spring exhibition exploring growth and renewal across painting and sculpture.',
    status: 'voting',
    coverUrl: null,
    artistCount: 24,
    votesCount: 1832,
    votingPeriod: period('2026-10-05T00:00:00.000Z', '2026-10-12T00:00:00.000Z'),
    heroMedia: { id: 'bloom-hero', url: 'https://cdn.needfulthings.com/media/bloom-hero.jpg' },
    media: {
      hero: { id: 'bloom-hero', url: 'https://cdn.needfulthings.com/media/bloom-hero.jpg' },
      gallery: [
        { id: 'still-water-01', url: 'https://cdn.needfulthings.com/media/still-water-01.jpg' },
        { id: 'quarry-light', url: 'https://cdn.needfulthings.com/media/quarry-light.jpg' },
      ],
    },
  },
  {
    id: 'concrete-seasons',
    createdAt: isoHoursAgo(24 * 40),
    updatedAt: isoHoursAgo(24),
    slug: 'concrete-seasons',
    name: 'Concrete Seasons',
    description: 'Four artists respond to the changing seasons of the built environment.',
    status: 'coming_soon',
    coverUrl: null,
    artistCount: 0,
    votesCount: 0,
    votingPeriod: period('2026-10-20T00:00:00.000Z', '2026-11-03T00:00:00.000Z'),
    heroMedia: null,
  },
  {
    id: 'paper-cities',
    createdAt: isoHoursAgo(24 * 60),
    updatedAt: isoHoursAgo(72),
    slug: 'paper-cities',
    name: 'Paper Cities',
    description: 'Intricate paper works mapping imagined urban landscapes.',
    status: 'winner_selected',
    coverUrl: null,
    artistCount: 18,
    votesCount: 2401,
    votingPeriod: period('2026-09-14T00:00:00.000Z', '2026-09-28T00:00:00.000Z'),
    heroMedia: null,
  },
  {
    id: 'tidal',
    createdAt: isoHoursAgo(24 * 90),
    updatedAt: isoHoursAgo(168),
    slug: 'tidal',
    name: 'Tidal',
    description: 'A study of water, erosion and the pull of the moon.',
    status: 'production',
    coverUrl: null,
    artistCount: 12,
    votesCount: 987,
    votingPeriod: period('2026-08-01T00:00:00.000Z', '2026-08-15T00:00:00.000Z'),
    heroMedia: null,
  },
  {
    id: 'night-garden',
    createdAt: isoHoursAgo(24 * 150),
    updatedAt: isoHoursAgo(720),
    slug: 'night-garden',
    name: 'Night Garden',
    description: 'Nocturnal botanicals rendered in light and shadow.',
    status: 'archive',
    coverUrl: null,
    artistCount: 31,
    votesCount: 5120,
    votingPeriod: period('2026-05-19T00:00:00.000Z', '2026-06-02T00:00:00.000Z'),
    heroMedia: null,
  },
  {
    id: 'salt-and-iron',
    createdAt: isoHoursAgo(24 * 40),
    updatedAt: isoHoursAgo(5),
    slug: 'salt-and-iron',
    name: 'Salt & Iron',
    description: 'Material studies in corrosion, salt and forged metal.',
    status: 'voting',
    coverUrl: null,
    artistCount: 21,
    votesCount: 1264,
    votingPeriod: period('2026-10-11T00:00:00.000Z', '2026-10-18T00:00:00.000Z'),
    heroMedia: null,
  },
  {
    id: 'paper-moon',
    createdAt: isoHoursAgo(24 * 10),
    updatedAt: isoHoursAgo(48),
    slug: 'paper-moon',
    name: 'Paper Moon',
    description: 'A whimsical collection of paper-cut lunar landscapes.',
    status: 'coming_soon',
    coverUrl: null,
    artistCount: 0,
    votesCount: 0,
    votingPeriod: period('2026-11-01T00:00:00.000Z', '2026-11-15T00:00:00.000Z'),
    heroMedia: null,
  },
  {
    id: 'verdant',
    createdAt: isoHoursAgo(24 * 70),
    updatedAt: isoHoursAgo(144),
    slug: 'verdant',
    name: 'Verdant',
    description: 'Lush botanical works celebrating untamed greenery.',
    status: 'winner_selected',
    coverUrl: null,
    artistCount: 27,
    votesCount: 3310,
    votingPeriod: period('2026-09-06T00:00:00.000Z', '2026-09-20T00:00:00.000Z'),
    heroMedia: null,
  },
  {
    id: 'static-bloom',
    createdAt: isoHoursAgo(24 * 100),
    updatedAt: isoHoursAgo(336),
    slug: 'static-bloom',
    name: 'Static Bloom',
    description: 'Digital flora blooming from signal noise.',
    status: 'production',
    coverUrl: null,
    artistCount: 15,
    votesCount: 1420,
    votingPeriod: period('2026-07-16T00:00:00.000Z', '2026-07-30T00:00:00.000Z'),
    heroMedia: null,
  },
  {
    id: 'low-tide',
    createdAt: isoHoursAgo(24 * 200),
    updatedAt: isoHoursAgo(1440),
    slug: 'low-tide',
    name: 'Low Tide',
    description: 'What remains on the shore when the water retreats.',
    status: 'archive',
    coverUrl: null,
    artistCount: 22,
    votesCount: 2890,
    votingPeriod: period('2026-04-28T00:00:00.000Z', '2026-05-12T00:00:00.000Z'),
    heroMedia: null,
  },
  {
    id: 'ember-field',
    createdAt: isoHoursAgo(24 * 30),
    updatedAt: isoHoursAgo(1),
    slug: 'ember-field',
    name: 'Ember Field',
    description: 'Heat, ash and the aftermath of fire in mixed media.',
    status: 'voting',
    coverUrl: null,
    artistCount: 19,
    votesCount: 754,
    votingPeriod: period('2026-10-18T00:00:00.000Z', '2026-10-25T00:00:00.000Z'),
    heroMedia: null,
  },
  {
    id: 'glasshouse',
    createdAt: isoHoursAgo(24 * 300),
    updatedAt: isoHoursAgo(2160),
    slug: 'glasshouse',
    name: 'Glasshouse',
    description: 'Fragile structures and cultivated light.',
    status: 'archive',
    coverUrl: null,
    artistCount: 29,
    votesCount: 4670,
    votingPeriod: period('2026-03-25T00:00:00.000Z', '2026-04-08T00:00:00.000Z'),
    heroMedia: null,
  },
];

/** Maps an API project DTO to the CMS view model. */
export function mapProject(dto: ProjectDto): Project {
  const hero = dto.heroMedia ?? dto.media?.hero ?? null;
  return {
    id: dto.id,
    slug: dto.slug,
    name: dto.name,
    description: dto.description ?? '',
    status: PROJECT_STATUS_LABEL[dto.status],
    artists: dto.artistCount ?? 0,
    votes: dto.votesCount ?? 0,
    period: formatVotingPeriod(dto.votingPeriod),
    heroMedia: hero,
    gallery: dto.media?.gallery ?? [],
    updated: relativeTime(dto.updatedAt),
    updatedHours: hoursSince(dto.updatedAt),
  };
}

/** Normalizes a project detail DTO (the detail uses `totalVotes`, not `votesCount`). */
function normalizeProjectDetail(dto: ProjectDto & { totalVotes?: number }): ProjectDto {
  return { ...dto, votesCount: dto.votesCount ?? dto.totalVotes ?? 0 };
}

/** Fetches a page of projects from the API and maps it to the view model. */
async function fetchProjects(params: ProjectQuery): Promise<PaginatedProjects> {
  const response = await sdk.api.admin.projects.get({
    query: {
      search: params.search,
      status:
        params.status && params.status !== 'All' ? PROJECT_STATUS_API[params.status] : undefined,
      order: params.order === 'name' ? 'name' : 'newest',
      page: params.page,
      pageSize: params.pageSize,
    },
  });
  const page = unwrap<Paginated<ProjectDto>>(response);
  return { ...page, items: page.items.map(mapProject) };
}

/** Persists a project's hero/gallery usages via `PUT /:id/media`. */
async function saveProjectMedia(
  id: string,
  hero: MediaRef | null | undefined,
  gallery: MediaRef[] | undefined,
): Promise<void> {
  await sdk.api.admin.projects({ id }).media.put({
    heroMediaId: hero?.id ?? null,
    galleryMediaIds: (gallery ?? []).map((item) => item.id),
  });
}

function slugify(value: string): string {
  return (
    value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'project'
  );
}

function orderProjects(items: ProjectDto[], order: OrderKey): ProjectDto[] {
  const sorted = [...items];
  switch (order) {
    case 'name':
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      break;
    case 'votes-desc':
      sorted.sort((a, b) => b.votesCount - a.votesCount);
      break;
    case 'votes-asc':
      sorted.sort((a, b) => a.votesCount - b.votesCount);
      break;
    default:
      sorted.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }
  return sorted;
}

function paginate(items: ProjectDto[], page: number, pageSize: number): PaginatedProjects {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;

  return {
    items: items.slice(start, start + pageSize).map(mapProject),
    total,
    page: safePage,
    pageSize,
    totalPages,
  };
}

export async function listProjects(params: ListProjectsParams = {}): Promise<PaginatedProjects> {
  if (!USE_MOCK) return fetchProjects(params);
  await delay();
  const { status = 'All', order = 'newest', page = 1, pageSize = PROJECT_PAGE_SIZE } = params;

  const filtered = db.filter(
    (project) => status === 'All' || mapProject(project).status === status,
  );
  return paginate(orderProjects(filtered, order), page, pageSize);
}

export async function searchProjects(
  term: string,
  params: ListProjectsParams = {},
): Promise<PaginatedProjects> {
  if (!USE_MOCK) return fetchProjects({ ...params, search: term });
  await delay();
  const { status = 'All', order = 'newest', page = 1, pageSize = PROJECT_PAGE_SIZE } = params;

  const query = term.trim().toLowerCase();
  const results = db.filter((project) => project.name.toLowerCase().includes(query));
  const filtered =
    status === 'All' ? results : results.filter((p) => mapProject(p).status === status);

  return paginate(orderProjects(filtered, order), page, pageSize);
}

export function queryProjects(params: ProjectQuery = {}): Promise<PaginatedProjects> {
  if (!USE_MOCK) return fetchProjects(params);
  const { search, ...rest } = params;
  return search?.trim() ? searchProjects(search, rest) : listProjects(rest);
}

export async function getProject(idOrSlug: string): Promise<Project | undefined> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.projects({ id: idOrSlug }).get();
    return mapProject(
      normalizeProjectDetail(unwrap<ProjectDto & { totalVotes?: number }>(response)),
    );
  }
  await delay();
  const dto = db.find((project) => project.id === idOrSlug || project.slug === idOrSlug);
  return dto ? mapProject(dto) : undefined;
}

/** Creates a voting period for a project (scheduled when dates are provided). */
async function createProjectVotingPeriod(
  projectId: string,
  opensAt: string,
  closesAt: string,
): Promise<void> {
  await sdk.api.admin['voting-periods'].post({
    projectId,
    mode: opensAt ? 'scheduled' : 'manual',
    opensAt: opensAt || undefined,
    closesAt: closesAt || undefined,
  });
}

export async function createProject(input: ProjectInput): Promise<Project> {
  if (!USE_MOCK) {
    const created = unwrap<ProjectDto>(
      await sdk.api.admin.projects.post({
        name: input.name,
        description: input.description,
        status: PROJECT_STATUS_API[input.status as ProjectStatus] ?? 'coming_soon',
      }),
    );
    if (input.votingStarts) {
      await createProjectVotingPeriod(created.id, input.votingStarts, input.votingEnds);
    }
    await saveProjectMedia(created.id, input.heroMedia, input.gallery);
    const detail = await sdk.api.admin.projects({ id: created.id }).get();
    return mapProject(normalizeProjectDetail(unwrap<ProjectDto & { totalVotes?: number }>(detail)));
  }
  await delay();

  let slug = slugify(input.name);
  while (db.some((project) => project.slug === slug)) {
    slug = `${slug}-1`;
  }

  const now = new Date().toISOString();
  const dto: ProjectDto = {
    id: crypto.randomUUID(),
    createdAt: now,
    updatedAt: now,
    slug,
    name: input.name,
    description: input.description,
    status: PROJECT_STATUS_API[input.status as ProjectStatus] ?? 'coming_soon',
    coverUrl: null,
    artistCount: 0,
    votesCount: 0,
    votingPeriod: input.votingStarts ? period(input.votingStarts, input.votingEnds || null) : null,
    heroMedia: input.heroMedia ?? null,
    media: { hero: input.heroMedia ?? null, gallery: input.gallery ?? [] },
  };

  db = [dto, ...db];
  await pushActivity({
    verb: 'created',
    entity: 'project',
    targetType: 'project',
    targetSlug: dto.slug,
    targetLabel: dto.name,
  });
  return mapProject(dto);
}

export async function archiveProject(id: string): Promise<Project> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.projects({ id }).archive.post();
    return mapProject(unwrap<ProjectDto>(response));
  }
  return updateProjectStatus(id, 'archive');
}

export async function restoreProject(id: string): Promise<Project> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.projects({ id }).restore.post();
    return mapProject(unwrap<ProjectDto>(response));
  }
  return updateProjectStatus(id, 'coming_soon');
}

async function updateProjectStatus(id: string, status: ProjectApiStatus): Promise<Project> {
  await delay();
  let updated: ProjectDto | undefined;
  db = db.map((project) => {
    if (project.id !== id) return project;
    updated = { ...project, status, updatedAt: new Date().toISOString() };
    return updated;
  });
  if (!updated) throw new Error(`Project ${id} not found`);
  await pushActivity({
    verb: status === 'archive' ? 'archived' : 'restored',
    entity: 'project',
    targetType: 'project',
    targetSlug: updated.slug,
    targetLabel: updated.name,
  });
  return mapProject(updated);
}

export async function updateProject(id: string, input: ProjectInput): Promise<Project> {
  if (!USE_MOCK) {
    await sdk.api.admin.projects({ id }).patch({
      name: input.name,
      description: input.description,
      status: PROJECT_STATUS_API[input.status as ProjectStatus],
    });

    // Create a voting period only when one doesn't exist yet (periods are
    // otherwise managed from the voting page).
    if (input.votingStarts) {
      const existing = unwrap<{ votingPeriod: unknown | null }>(
        await sdk.api.admin.projects({ id }).get(),
      );
      if (!existing.votingPeriod) {
        await createProjectVotingPeriod(id, input.votingStarts, input.votingEnds);
      }
    }

    await saveProjectMedia(id, input.heroMedia, input.gallery);
    const detail = await sdk.api.admin.projects({ id }).get();
    return mapProject(normalizeProjectDetail(unwrap<ProjectDto & { totalVotes?: number }>(detail)));
  }
  await delay();
  let updated: ProjectDto | undefined;

  db = db.map((project) => {
    if (project.id !== id) return project;
    updated = {
      ...project,
      name: input.name,
      description: input.description,
      status: PROJECT_STATUS_API[input.status as ProjectStatus] ?? project.status,
      votingPeriod: input.votingStarts
        ? period(input.votingStarts, input.votingEnds || null)
        : project.votingPeriod,
      heroMedia: input.heroMedia ?? null,
      media: { hero: input.heroMedia ?? null, gallery: input.gallery ?? [] },
      updatedAt: new Date().toISOString(),
    };
    return updated;
  });

  if (!updated) throw new Error(`Project ${id} not found`);
  await pushActivity({
    verb: 'updated',
    entity: 'project',
    targetType: 'project',
    targetSlug: updated.slug,
    targetLabel: updated.name,
  });
  return mapProject(updated);
}
